import type { Bundle, ChildBundleHorseVariant, IHorseVariant, QueryParams } from "../../api_utils/types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActionList,
  BlockStack,
  Box,
  Button,
  Card,
  EmptyState,
  Icon,
  IndexTable,
  InlineStack,
  Layout,
  Page,
  PageActions,
  Popover,
  SkeletonBodyText,
  SkeletonPage,
  Text,
  TextField,
} from "@shopify/polaris";
import { MenuHorizontalIcon, EditIcon, XIcon } from "@shopify/polaris-icons";
import { isEqual, getIdFromPath, extractMessageFromError, cloneNestedObjects } from "../../helper_functions/utils";
import AddVariantDialog from "../common/AddVariantDialog";
import { SelectVariantDialog } from "../common/SelectVariantDialog";
import DeleteConfirmationDialog from "../common/DeleteConfirmationDialog";
import HorseVariant from "../common/HorseVariant/HorseVariant";
import SettingToggle from "../common/SettingToggle";
import {
  deleteBundle,
  getBundle,
  updateBundle,
  updateBundleSelection,
  getBundleHorseVariants,
  removeBundleHorseVariant,
  removeAllBundleHorseVariants,
  useBundleHorseVariantSelectedIds,
} from "../../api_utils/requests";
import { ErrorBanner } from "../common/ErrorBanner";
import { Footer } from "../common/Footer";
import type { NonEmptyArray } from "@shopify/polaris/build/ts/src/types";
import type { IndexTableHeading } from "@shopify/polaris/build/ts/src/components/IndexTable";
import SaveBar from "../common/SaveBar";
import { useNavigate } from "react-router-dom";
import type { QueryKey } from "@tanstack/react-query";

const bundlesHelpPageUrl = "https://horse-inventory.notion.site/Bundles-88ac584381a445de890d4f68870d5920";
const helpUrl =
  "https://horse-inventory.notion.site/Bundles-88ac584381a445de890d4f68870d5920?pvs=4#30886ee8a00f4c35b26620d83910eee4";

type ChildBundleHorseVariants = Record<number, ChildBundleHorseVariant>;

const formatBundleHorseVariants = (bundleHorseVariants: ChildBundleHorseVariant[]): ChildBundleHorseVariants =>
  bundleHorseVariants.reduce<ChildBundleHorseVariants>((map, horseVariant) => {
    map[horseVariant.id] = horseVariant;
    return map;
  }, {});

const formatBundleHorseVariantsToSave = (bundleHorseVariants: ChildBundleHorseVariants): ChildBundleHorseVariant[] =>
  Object.values(bundleHorseVariants).map((horseVariant) => horseVariant);

const columnNames: NonEmptyArray<IndexTableHeading> = [{ title: "Variant" }, { title: "Quantity" }, { title: "" }];

const emptyState = (
  <EmptyState heading="No variants added" image="">
    <p>You have not added any variants to your bundle yet.</p>
  </EmptyState>
);

function ShowBundle(): React.JSX.Element {
  const bundleId = useMemo(() => getIdFromPath(), []);

  const [bundle, setBundle] = useState<Bundle>();
  const originalBundle = useRef<Bundle>();

  const [bundleHorseVariants, setBundleHorseVariants] = useState<ChildBundleHorseVariants>(
    {} as ChildBundleHorseVariants,
  );
  const originalBundleHorseVariants = useRef<ChildBundleHorseVariants>({} as ChildBundleHorseVariants);

  const [bundleHorseVariant, setBundleHorseVariant] = useState<IHorseVariant>();
  const originalBundleHorseVariant = useRef<IHorseVariant>();

  const [loading, setLoading] = useState(true);
  useEffect(() => {
    shopify.loading(loading);
  }, [loading]);
  const [addVariantModalActive, setAddVariantModalActive] = useState(false);
  const [popoverActive, setPopoverActive] = useState(false);
  const [selectVariantModalActive, setSelectVariantModalActive] = useState(false);
  const [deletionModalActive, setDeletionModalActive] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const isDirty = !isEqual(bundle, originalBundle.current) || !isEqual(bundleHorseVariants, originalBundleHorseVariants.current);

  const togglePopoverActive = useCallback(() => {
    setPopoverActive((currentPopoverActive) => !currentPopoverActive);
  }, []);

  useEffect(() => {
    getBundle(bundleId)
      .then(({ bundle: newBundle, bundleHorseVariants: newBundleHorseVariants }) => {
        setBundle(newBundle);
        originalBundle.current = { ...newBundle };

        setBundleHorseVariant(newBundle.horseVariant);
        originalBundleHorseVariant.current = { ...newBundle.horseVariant };

        const formattedHorseVariants = formatBundleHorseVariants(newBundleHorseVariants);
        setBundleHorseVariants(formattedHorseVariants);
        originalBundleHorseVariants.current = cloneNestedObjects(formattedHorseVariants);

        setLoading(false);
      })
      .catch((error: unknown) => {
        console.error("Error fetching data:", error);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBundleChange = useCallback((value: string | boolean, key: string) => {
    setBundle((prevBundle) => {
      return { ...prevBundle, [key]: value };
    });
  }, []);

  const handleHorseVariantUpdate = (horseVariant: IHorseVariant): void => {
    setBundleHorseVariant(horseVariant);
    setBundle({ ...bundle, horse_variant_id: horseVariant.id });
  };

  const handleVariantUpdate = useCallback(async () => {
    let newBundleHorseVariants: ChildBundleHorseVariant[] = [];
    try {
      newBundleHorseVariants = await getBundleHorseVariants(bundleId);
    } catch (err: unknown) {
      const message = extractMessageFromError(err);
      setErrorMessage(message);
      console.error("Error in handleVariantUpdate:", message);
      return;
    }

    const updatedVariants = {...bundleHorseVariants};
    const newVariantsMap = formatBundleHorseVariants(newBundleHorseVariants);

    // Add only new variants (not already in existingVariants)
    Object.entries(newVariantsMap).forEach(([horseVariantId, variant]) => {
      if (!updatedVariants[horseVariantId]) {
        updatedVariants[horseVariantId] = variant;
      }
    });

    // Remove variants that are no longer in the API response (deselected)
    Object.keys(updatedVariants).forEach((horseVariantId) => {
      if (!newVariantsMap[horseVariantId]) {
        delete updatedVariants[horseVariantId];
      }
    });

    // Merge new variants with existing ones, avoiding duplicates by horse_variant_id
    setBundleHorseVariants(updatedVariants);
    originalBundleHorseVariants.current = newVariantsMap;
  }, [bundleId, bundleHorseVariants]);

  const handleHorseVariantsQuantityChange = useCallback(
    (variantId: number, newQuantity: string, key: string) => {
      const newBundleHorseVariants = JSON.parse(JSON.stringify(bundleHorseVariants)) as ChildBundleHorseVariants;
      const cleanQuantity = newQuantity?.replace(/[^\d.-]/g, "");
      newBundleHorseVariants[variantId][key] = cleanQuantity;
      setBundleHorseVariants(newBundleHorseVariants);
    },
    [bundleHorseVariants],
  );

  const handleSaveBundle = useCallback(() => {
    setLoading(true);

    const payload = {
      bundle: {
        ...bundle,
        bundle_horse_variants_attributes: formatBundleHorseVariantsToSave(bundleHorseVariants),
      },
    };
    updateBundle(bundleId, payload)
      .then((updatedBundle) => {
        shopify.toast.show("Save successful");

        setBundle(updatedBundle);
        originalBundle.current = { ...updatedBundle };

        setBundleHorseVariant(updatedBundle.horseVariant);
        originalBundleHorseVariant.current = { ...updatedBundle.horseVariant };

        originalBundleHorseVariants.current = cloneNestedObjects(bundleHorseVariants);
        setErrorMessage("");
      })
      .catch((err: unknown) => {
        Rollbar.error(err, { bundleId, payload });
        const newErrorMessage = extractMessageFromError(err);
        setErrorMessage(newErrorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [bundle, bundleHorseVariants, bundleId]);

  const removeAllVariants = useCallback(async () => {
    setLoading(true);
    setBundleHorseVariants({} as ChildBundleHorseVariants);
    try {
      await removeAllBundleHorseVariants(bundleId)
      originalBundleHorseVariants.current = {} as ChildBundleHorseVariants;
    } catch (err: unknown) {
      setBundleHorseVariants(cloneNestedObjects(originalBundleHorseVariants.current));
      Rollbar.error(err, { bundleId });
      const newErrorMessage = extractMessageFromError(err);
      setErrorMessage(newErrorMessage);
    } finally {
      setLoading(false);
    }
  }, [bundleId]);

  const removeVariant = useCallback(
    (id: number) => {
      const newBundleHorseVariants = { ...bundleHorseVariants };
      delete newBundleHorseVariants[id];
      setBundleHorseVariants(newBundleHorseVariants);
      removeBundleHorseVariant(id)
        .then(() => {
          shopify.toast.show("Variant removed");
          originalBundleHorseVariants.current = cloneNestedObjects(newBundleHorseVariants);
        })
        .catch((err: unknown) => {
          Rollbar.error(err, { bundleId, variantId: id });
          const newErrorMessage = extractMessageFromError(err);
          setErrorMessage(newErrorMessage);
          setBundleHorseVariants(cloneNestedObjects(originalBundleHorseVariants.current));
        });
    },
    [bundleHorseVariants, originalBundleHorseVariants, bundleId],
  );

  const handleDiscardAction = useCallback(() => {
    setBundle(originalBundle.current);
    setBundleHorseVariant(originalBundleHorseVariant.current);
    setBundleHorseVariants(cloneNestedObjects(originalBundleHorseVariants.current));
  }, [originalBundle, originalBundleHorseVariant, originalBundleHorseVariants]);

  const handleUpdateSelection = useCallback(
    async (
      payload: { bundle_horse_variants_attributes: { horse_variant_id: number }[] },
      queryParams: QueryParams,
    ): Promise<{ horse_variant_ids: number[]; errors?: string[] }> => {
      const updatePromise = updateBundleSelection(bundleId, payload, queryParams);
      void updatePromise.then(({ errors }) => {
        if (errors) {
          setErrorMessage(errors.join("\n"));
        }
      });
      return updatePromise;
    },
    [bundleId, setErrorMessage],
  );

  const activator = (
    <Button accessibilityLabel="Menu" disclosure icon={MenuHorizontalIcon} onClick={togglePopoverActive} />
  );

  const pageSkeleton = (
    <SkeletonPage>
      <Layout>
        <Layout.Section variant="oneThird">
          <BlockStack gap="400">
            <Card>
              <SkeletonBodyText lines={2} />
            </Card>
            <Card>
              <SkeletonBodyText lines={5} />
            </Card>
          </BlockStack>
        </Layout.Section>
        <Layout.Section>
          <Card>
            <SkeletonBodyText lines={10} />
          </Card>
        </Layout.Section>
      </Layout>
    </SkeletonPage>
  );

  const selectedIdsQueryKey: QueryKey = ["bundle_horse_variant_selected_ids", bundleId];
  const querySelected = useBundleHorseVariantSelectedIds(bundleId, {
    enabled: false,
    queryKey: selectedIdsQueryKey,
  });

  const navigate = useNavigate();

  const pageMarkup = (
    <Page
      backAction={{
        content: "Bundles",
        onAction: async () => {
          await shopify.saveBar.leaveConfirmation();

          navigate("/bundles");
        },
      }}
      title={bundle?.name}
    >
      <SaveBar handleDiscard={handleDiscardAction} handleSave={handleSaveBundle} isDirty={isDirty} />

      <SelectVariantDialog
        active={selectVariantModalActive}
        handleDiscard={() => {
          setBundleHorseVariant({ ...originalBundleHorseVariant.current });
        }}
        selected={bundleHorseVariant}
        setActive={setSelectVariantModalActive}
        updateSelection={handleHorseVariantUpdate}
      />
      <Layout>
        {errorMessage ? (
          <Layout.Section variant="fullWidth">
            <ErrorBanner errorMessage={errorMessage} setErrorMessage={setErrorMessage} />
          </Layout.Section>
        ) : null}
        <Layout.Section variant="oneThird">
          <BlockStack gap="400">
            <Card>
              <TextField autoComplete="off" id="name" label="Name" onChange={handleBundleChange} value={bundle?.name} />
            </Card>
            <Card>
              <Text as="h6" variant="headingSm">
                Bundle parent variant
              </Text>
              <Box paddingBlock="200">
                <Text as="h6" variant="bodySm">
                  The variant that the customer buys
                </Text>
              </Box>
              <BlockStack gap="200">
                <InlineStack align="space-between" blockAlign="center">
                  {bundleHorseVariant ? <HorseVariant variant={bundleHorseVariant} /> : null}
                  <Popover
                    activator={activator}
                    active={popoverActive}
                    autofocusTarget="first-node"
                    onClose={togglePopoverActive}
                  >
                    <ActionList
                      actionRole="menuitem"
                      items={[
                        {
                          content: "Edit parent bundle variant",
                          icon: EditIcon,
                          onAction: (): void => {
                            setSelectVariantModalActive(true);
                          },
                        },
                      ]}
                    />
                  </Popover>
                </InlineStack>
              </BlockStack>
            </Card>
            <Card>
              <SettingToggle
                description="Automatically adjust the inventory of the parent according to the available quantities of its children."
                enabled={bundle?.parent_inventory_tracks_children}
                handleToggle={() => {
                  handleBundleChange(!bundle?.parent_inventory_tracks_children, "parent_inventory_tracks_children");
                }}
                helpLink={helpUrl}
                title="Track parent quantity"
              />
            </Card>
          </BlockStack>
        </Layout.Section>
        <Layout.Section>
          <Card padding="0">
            <Box padding="400">
              <Text as="h6" variant="headingSm">
                Add child variants to the bundle
              </Text>
              <Box paddingBlock="200">
                <Text as="h6" variant="bodySm">
                  These variants are inside the bundle
                </Text>
              </Box>
            </Box>
            <BlockStack gap="200">
              <InlineStack align="center">
                <AddVariantDialog
                  active={addVariantModalActive}
                  excludableHorseVariantIds={[bundle?.horse_variant_id]}
                  handleVariantUpdate={handleVariantUpdate}
                  primary
                  queryKey={selectedIdsQueryKey}
                  querySelected={querySelected}
                  removeAllVariants={removeAllVariants}
                  selectionKey="bundle_horse_variants_attributes"
                  setActive={setAddVariantModalActive}
                  setErrorMessage={setErrorMessage}
                  updateSelection={handleUpdateSelection}
                />
              </InlineStack>
              <IndexTable
                emptyState={emptyState}
                headings={columnNames}
                itemCount={Object.keys(bundleHorseVariants).length}
                selectable={false}
              >
                {Object.values(bundleHorseVariants).map((variant: ChildBundleHorseVariant, index) => (
                  <IndexTable.Row id={variant.id.toString()} key={variant.id} position={index}>
                    <IndexTable.Cell>
                      <HorseVariant key={variant.id} variant={variant} />
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <div className="w120">
                        <TextField
                          autoComplete="off"
                          id="quantity"
                          label="Quantity"
                          labelHidden
                          min={1}
                          name="Quantity"
                          onChange={(value: string, key: string): void => {
                            handleHorseVariantsQuantityChange(variant.id, value, key);
                          }}
                          type="number"
                          value={String(variant.quantity ?? 0)}
                        />
                      </div>
                    </IndexTable.Cell>
                    <IndexTable.Cell>
                      <Button
                        icon={<Icon source={XIcon} />}
                        onClick={(): void => {
                          removeVariant(variant.id);
                        }}
                        variant="plain"
                      />
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
      <PageActions
        secondaryActions={[
          {
            content: "Delete",
            destructive: true,
            onAction: () => {
              setDeletionModalActive(true);
            },
          },
        ]}
      />
      <DeleteConfirmationDialog
        active={deletionModalActive}
        content="Are you sure you want to delete this bundle? This action cannot be reversed."
        deleteItem={deleteBundle}
        gotoPath="/bundles"
        invalidateQueries={{
          queryKey: ["bundles", { page: 1 }],
        }}
        itemId={bundleId}
        setActive={setDeletionModalActive}
        title="Delete bundle"
        toastMessage="Bundle deleted"
      />
      <Footer pageTitle="bundles" url={bundlesHelpPageUrl} />
    </Page>
  );

  return loading ? pageSkeleton : pageMarkup;
}

export default ShowBundle;
